<template>
  <div :class="cn('tw:block', $attrs.class as any)" :style="$attrs.style as any">
    <label v-if="label" :for="groupId" class="tw:mb-1.5 tw:block tw:text-13 tw:font-medium tw:text-gray-700">
      {{ label }}
      <span v-if="isRequired" aria-hidden="true" class="tw:text-red-600">&nbsp;*</span>
    </label>

    <div
      :id="groupId"
      role="radiogroup"
      :class="cn('tw:flex', vertical ? 'tw:flex-col tw:gap-1' : 'tw:flex-row tw:gap-4')">
      <label
        v-for="option in options"
        :key="String(option.value)"
        :class="
          cn(
            'tw:group/radio tw:inline-flex tw:cursor-pointer tw:items-center tw:text-13 tw:text-gray-900',
            option.disabled && 'tw:cursor-not-allowed tw:opacity-50'
          )
        "
        :data-testid="option.testId">
        <!-- ring + inner dot. Native input lives inside so it doesn't take its own
             flex slot (which would break the label gap). `peer` lets the ring render
             a focus shadow on keyboard focus without JS. -->
        <span
          :class="
            cn(
              'tw:relative tw:inline-flex tw:size-4 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:border tw:border-solid tw:bg-white tw:transition-colors',
              isSelected(option.value)
                ? 'tw:border-primary tw:bg-primary'
                : 'tw:border-gray-300 tw:group-hover/radio:border-gray-400',
              'tw:has-[:focus-visible]:shadow-[0_0_0_4px_rgb(0_122_255/0.1)]'
            )
          ">
          <input
            type="radio"
            class="tw:sr-only"
            :name="groupName"
            :value="String(option.value)"
            :checked="isSelected(option.value)"
            :disabled="option.disabled"
            @change="select(option.value)" />
          <span
            aria-hidden="true"
            :class="
              cn(
                'tw:block tw:size-1.5 tw:rounded-full tw:bg-white tw:transition-transform',
                isSelected(option.value) ? 'tw:scale-100' : 'tw:scale-0'
              )
            " />
        </span>

        <span class="tw:ml-2.5 tw:min-w-0">{{ option.label }}</span>
      </label>
    </div>

    <span v-if="firstError" class="tw:mt-1.5 tw:block tw:text-13 tw:font-normal tw:text-red-600">
      {{ firstError }}
    </span>
    <span v-else-if="$slots.hint" class="tw:mt-1.5 tw:block tw:text-13 tw:font-normal tw:text-gray-600">
      <slot name="hint" />
    </span>
  </div>
</template>

<script lang="ts">
export interface LeadRadioGroupOption<V extends string | number = string | number> {
  value: V;
  label: string;
  disabled?: boolean;
  testId?: string;
}
</script>

<script setup lang="ts" generic="T extends string | number">
// LeadRadioGroup — options-driven radio group in the LEAD style. Native
// <input type="radio"> stays in the DOM (visually hidden) so form submission,
// keyboard nav, and screen readers work; the visible ring/dot is a sibling
// <span> driven by :checked via the `peer` utility.
import { computed, useId } from 'vue';
import { cn } from '@/lib/cn';

defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    options: LeadRadioGroupOption<T>[];
    label?: string;
    errors?: string[];
    isRequired?: boolean;
    /** Stack options vertically (default) vs. inline on one row. */
    vertical?: boolean;
    /** Shared `name` for the underlying inputs. Auto-generated if omitted. */
    name?: string;
  }>(),
  {
    label: undefined,
    errors: () => [],
    isRequired: false,
    vertical: true,
    name: undefined,
  }
);

const modelValue = defineModel<T>({ required: true });

/* --------------------------------- COMPUTED ------------------------------- */

const generatedId = useId();
const groupId = computed(() => `lead-radio-group-${generatedId}`);
const groupName = computed(() => props.name ?? groupId.value);

const firstError = computed(() => props.errors?.[0] ?? '');

/* --------------------------------- METHODS -------------------------------- */

function isSelected(value: T): boolean {
  return modelValue.value === value;
}

function select(value: T) {
  modelValue.value = value;
}
</script>
